// src/screens/HomeScreen.js

import React, { useState } from 'react';
import { writeBatch, doc, collection, serverTimestamp, getDocs } from 'firebase/firestore';
import { ShoppingBagIcon, PackagePlusIcon } from '../components/Icons';
import { getGeoFromCEP } from '../utils/helpers';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
// ✅ NEW: Import the LoadingScreen component
import LoadingScreen from './LoadingScreen';
import AdSlot from '../components/AdSlot';
import { db } from '../firebase'; // Ensure db is imported if not already

export default function HomeScreen({ setModal }) {
    const navigate = useNavigate();
    // ✅ STEP 1: Get the complete auth state from the hook
    const { profile, loading: authLoading, validationStatus } = useAuth();
    
    const [isAddingSamples, setIsAddingSamples] = useState(false);

    // This is the developer function to add sample data. It is preserved.
    const addSampleData = async () => {
        // This check is now safer because the component won't render until profile exists
        if (!profile?.id || !profile?.email) {
            setModal({ message: "Erro: Informações de usuário incompletas." });
            return;
        }

        setIsAddingSamples(true);
        setModal({
            message: "Adicionando dados de amostra...", isLoading: true,
        });

        try {
            const categoriesSnapshot = await getDocs(collection(db, "categories"));
            const categories = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            const findCat = (name) => categories.find(c => c.name === name);

            const batch = writeBatch(db);
            const sampleItems = [
                { title: "Violão Usado (SP)", credits: 250, cep: "01311-000", category: findCat("Instrumentos Musicais")?.name, subcategory: "Cordas", imageUrls: ["https://placehold.co/600x400/fca5a5/450a0a?text=Viol%C3%A3o+1", "https://placehold.co/600x400/fca5a5/450a0a?text=Viol%C3%A3o+2"] },
                { title: "Cadeira de Escritório (SP)", credits: 150, cep: "04538-133", category: findCat("Móveis")?.name, subcategory: "Escritório", imageUrls: ["https://placehold.co/600x400/93c5fd/1e3a8a?text=Cadeira"] },
                { title: "Bicicleta Caloi 10 (RJ)", credits: 400, cep: "22071-060", category: findCat("Esportes")?.name, subcategory: "Ciclismo", imageUrls: ["https://placehold.co/600x400/fdba74/7c2d12?text=Bicicleta"] },
                { title: "Livro 'Dom Casmurro' (BH)", credits: 20, cep: "30140-071", category: findCat("Livros")?.name, subcategory: "Literatura Brasileira", imageUrls: ["https://placehold.co/600x400/a5b4fc/3730a3?text=Livro"] },
                { title: "Monitor Dell 24' (Curitiba)", credits: 600, cep: "80230-010", category: findCat("Eletrônicos")?.name, subcategory: "Monitores", imageUrls: ["https://placehold.co/600x400/6ee7b7/065f46?text=Monitor"] },
            ];

            for (const item of sampleItems) {
                if (!item.category) continue;
                const location = await getGeoFromCEP(item.cep);
                if (location) {
                    const itemRef = doc(collection(db, "items"));
                    const itemData = {
                        ...item,
                        ownerId: profile.id,
                        ownerEmail: profile.email,
                        location,
                        status: 'available',
                        createdAt: serverTimestamp()
                    };
                    batch.set(itemRef, itemData);
                }
                // Rate limit the external API calls
                await new Promise(resolve => setTimeout(resolve, 1100));
            }
            await batch.commit();
            setModal({
                message: "Dados de amostra adicionados! Vá para o Escambo Center para vê-los e testar a busca por localização.",
                onConfirm: () => {
                    setModal(null);
                    navigate('/market');
                },
                confirmText: "Ir para o Escambo Center"
            });
        } catch (error) {
            console.error("Error adding sample data:", error);
            setModal({ message: `Ocorreu um erro: ${error.message}` });
        } finally {
            setIsAddingSamples(false);
        }
    };

    const ActionButton = ({ icon: Icon, text, onClick }) => (
        <button
            onClick={onClick}
            className="bg-white p-8 rounded-xl shadow-md flex flex-col items-center justify-center space-y-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-40"
        >
            <Icon className="w-12 h-12 text-green-600" />
            <span className="text-lg font-semibold text-gray-700">{text}</span>
        </button>
    );

    // ✅ STEP 2: Implement the robust Guard Clause
    if (authLoading || validationStatus !== 'success' || !profile) {
        return <LoadingScreen />;
    }

    return (
        <div>
            {/* Welcome Banner */}
            <div className="bg-green-600 text-white p-6 rounded-xl mb-8 text-center shadow-lg">
                <h1 className="text-2xl font-bold">Bem-vindo(a), {profile?.displayName || profile.email.split('@')[0]}!</h1>
                <p className="mt-1">Pronto para descobrir novas oportunidades de trocas hoje?</p>
            </div>

            {/* Platform ad (admin-controlled) */}
            <AdSlot placement="home_banner" className="mb-8" />

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <ActionButton
                    icon={ShoppingBagIcon}
                    text="Ver Oportunidades"
                    onClick={() => navigate('/market')}
                />
                <ActionButton
                    icon={PackagePlusIcon}
                    text="Anunciar Item"
                    onClick={() => navigate('/add-item')}
                />
            </div>

            {/* Developer-only button */}
            {process.env.NODE_ENV === 'development' && profile?.role === 'admin' && (
                <div className="mt-8 text-center">
                    <button onClick={addSampleData} disabled={isAddingSamples} className="text-sm bg-gray-200 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:bg-gray-300 disabled:cursor-not-allowed">
                        {isAddingSamples ? "Adicionando..." : "Adicionar Itens de Amostra (Dev)"}
                    </button>
                </div>
            )}
        </div>
    );
}

